import { Injectable } from '@angular/core'
import BARTER_STATE from '../../../constants/barter-state.constant'
import BARTER_PARCEL_TYPE from '../../../constants/barter-parcel-type.constant'
import BARTER_STATE_LABEL from '../../../constants/barter-state-label.constant'
import VERSION_TYPE from '../../../constants/versionType.constant'
import cloneDeep from 'lodash/cloneDeep'

@Injectable({
    providedIn: 'root'
})
export class BarterService {

    public static STATE_VALID = {value: BARTER_STATE.valid, name: BARTER_STATE_LABEL.valid}
    public static STATE_PROPOSAL = {value: BARTER_STATE.proposal, name: BARTER_STATE_LABEL.proposal}
    public static STATE_FINISHED = {value: BARTER_STATE.finished, name: BARTER_STATE_LABEL.finished}
    public static STATE_INVALID = {value: BARTER_STATE.postponed, name: BARTER_STATE_LABEL.postponed}
    public static UNIT_YEAR = {value: 'Y', name: 'let'}
    public static UNIT_MONTH = {value: 'M', name: 'měsíců'}

    getStates(): any[] {
        return [BarterService.STATE_VALID, BarterService.STATE_PROPOSAL, BarterService.STATE_FINISHED, BarterService.STATE_INVALID]
    }

    /**
     * Add parcel to list of barter parcels (concernedParcels or counterpartyParcels)
     * @param barterParcels
     * @param parcel
     * @param acreages
     */
    addParcelToBarterParcels(parcelGroups, parcel, acreages, barterId): void {
        let foundParcel, foundGroup
        if (!parcelGroups) {
            parcelGroups = []
        }

        parcelGroups.forEach(group => {
            if (group.lv === parcel.lv && group.katuzeKod === parcel.katuzeKod) {
                foundGroup = group
            }

            group.parcels.forEach((groupParcel) => {
                if (groupParcel.parcel.parId === parcel.parId) {
                    groupParcel.parcel = parcel
                    foundParcel = true
                    const updatedAcreages = this.removeBarterParcelAcreagesByParcel(groupParcel, acreages) // Create an updated array
                    acreages.length = 0 // Mutate original array
                    acreages.push(...updatedAcreages) // Mutate original array
                }
            })
        })

        if (!foundParcel) {
            if (!foundGroup) {
                foundGroup = {lv: parcel.lv, katuzeKod: parcel.katuzeKod, parcels: []}
                parcelGroups.push(foundGroup)
            }

            const newBarterParcel = {
                parcel: parcel,
                parcelId: parcel.id,
                parId: parcel.parId,
                errorHidden: false,
                barterId: barterId
            }

            foundGroup.parcels.push(newBarterParcel)
            const updatedAcreages = this.removeBarterParcelAcreagesByParcel(newBarterParcel, acreages) // Create an updated array
            acreages.length = 0 // Mutate original array
            acreages.push(...updatedAcreages) // Mutate original array
        }
    }

    /**
     * Add received parcelUsedAcreage to barterParcel(Concerned|Counterparty)Acreage list
     * @param barterParcelAcreages
     * @param parcelUsedAcreage
     * @param type
     * @param concernedSubject
     * @param counterparty
     */
    addParcelAcreageToBarterParcelAcreages(barterParcelAcreages, parcelUsedAcreage, type, concernedSubject, counterparty): void {
        let found = false

        //add acreages only for parts intersecting with field block of corresponding barer side subject
        if (this.isContextLpisUser(type, concernedSubject, counterparty, parcelUsedAcreage)) {
            barterParcelAcreages.forEach((value) => {
                if (value.parcel && value.parcel.parId === parcelUsedAcreage.parcel.parId && value.lpisKultura.id === parcelUsedAcreage.lpisKultura.id) {
                    const usableVectorAcreage = this.getUsableParcelUsedAcreage(type, parcelUsedAcreage, concernedSubject, counterparty)
                    value.acreage += (usableVectorAcreage > parcelUsedAcreage.parcel.vymera) ? parcelUsedAcreage.parcel.vymera : usableVectorAcreage
                    value.parcel = parcelUsedAcreage.parcel
                    found = true
                }
            })

            if (!found) {
                barterParcelAcreages.push({
                    parcel: parcelUsedAcreage.parcel,
                    parcelId: parcelUsedAcreage.parcelId,
                    parId: parcelUsedAcreage.parId,
                    lpisKultura: parcelUsedAcreage.lpisKultura,
                    lpisUserId: parcelUsedAcreage.lpisUserId,
                    acreage: this.getUsableParcelUsedAcreage(type, parcelUsedAcreage, concernedSubject, counterparty)
                })
            }
        }
    }

    addParcelGeometryToBarterParcelGeometries(barterParcelGeometries, addedGeom): void {
        let found = false

        barterParcelGeometries.forEach( value => {
            if (value.parcelId && value.parcelId === addedGeom.parcelId && value.id === addedGeom.id) {
                value.geom = addedGeom.geom
                found = true
            }
        })

        if (!found) {
            barterParcelGeometries.push({
                parcelId: addedGeom.parcelId,
                id: addedGeom.id,
                geom: addedGeom.geom,
            })
        }
    }

    /**
     * Return subject related to given barter side type
     * @param type
     * @param concernedSubject
     * @param counterparty
     * @returns {number}
     */
    getContextLpisUser(type, concernedSubject, counterparty): string {
        return (type === BARTER_PARCEL_TYPE.counterparty || type === BARTER_PARCEL_TYPE.passed) ? concernedSubject.lpisId : counterparty.lpisId
    }

    /**
     * If type concerned then return usedAcreage of parcelUsedAcreage only if parcelUsedAcreage is related to counterparty.
     * If type counterparty then return usedAcreage of parcelUsedAcreage only if parcelUsedAcreage is related to concerned.
     *
     * @param type
     * @param parcelUsedAcreage
     * @param concernedSubject
     * @param counterparty
     * @returns {*}
     */
    getUsableParcelUsedAcreage(type, parcelUsedAcreage, concernedSubject, counterparty): number {
        return ((type === BARTER_PARCEL_TYPE.counterparty && concernedSubject)
            || (type === BARTER_PARCEL_TYPE.passed && concernedSubject)
            || (type === BARTER_PARCEL_TYPE.concerned && counterparty)
            || (type === BARTER_PARCEL_TYPE.received && counterparty)
        ) ? parcelUsedAcreage.acreage : 0
    }

    getBarterParcelFromGroups(barterParcels: any[]): any[] {
        return barterParcels?.flatMap(group => group.parcels)
    }

    /**
     * Get list of ids from list of barter parcels (concernedParcels or counterpartyParcels)
     * @param barterParcels
     * @returns {Array}
     */
    getBarterParcelIds(barterParcels): number[] {
        return barterParcels.flatMap(group => group.parcels.map(parcel => parcel.parcelId))
    }

    /**
     * @param barter
     * @returns {Array}
     */
    getBarterValidationConflicts(barter): any[] {
        const list = []
        if (!barter.efficiencyFromDate) {
            list.push('Není vyplněno datum účinnosti smlouvy.')
        }
        if (!barter.concernedSubject) {
            list.push('Není vybrána moje farma.')
        }
        if (!barter.counterparty) {
            list.push('Není vybrána protistrana.')
        }
        return list
    }

    /**
     * Get array of barter parcel acreages for given parcel
     * @param barterParcel
     * @param acreages
     * @returns {Array}
     */
    getBarterParcelAcreagesForParcel(barterParcel, acreages): any[] {
        return acreages ? acreages.filter(acreage => acreage?.parcelId === barterParcel?.parcelId) : []
    }

    /**
     * Remove barter parcel acrages for given barter parcel
     * @param barterParcel
     * @param acreages
     */
    removeBarterParcelAcreagesByParcel(barterParcel, acreages: any[]): any[] {
        return [...acreages.filter(acreage => acreage.parcelId !== barterParcel.parcelId)]
    }

    /**
     * Get sum of used acreages for given list of acreages
     * @param acreages
     * @returns {*}
     */
    getBarterParcelAcreagesSum(acreages: any[]): number {
        return acreages.reduce((sum, acreage) => sum + this.toAcreageNumber(acreage.acreage), 0)
    }

    /**
     * Get string of lpis kultura names delimited by string
     * @param acreages
     * @returns {string}
     */
    getBarterParcelAcreagesKulturaNames(acreages: any[]): string {
        return acreages.map(value => value.lpisKultura.name).join(', ')
    }

    /**
     * Normalize barter acreages - ceil by cadastral acreage
     * or add default acreages
     * @param acreages
     * @param barterParcels
     * @param contextSubjectLpisId
     * @param addedAcreages
     */
    normalizeParcelAcreages(acreages, barterParcels, contextSubjectLpisId, addedAcreages?): void {
        //search for parcel without acreages and set the default ones
        barterParcels.forEach( (group) => {
            group.parcels.forEach( barterParcel => {
                const acreagesForParcel = this.getBarterParcelAcreagesForParcel(barterParcel, acreages)
                const addedAcreagesForParcel = this.getBarterParcelAcreagesForParcel(barterParcel, addedAcreages)
                if (addedAcreagesForParcel?.length > 0) {
                    //if lpisAcreage > 0 (any lpis intersect exists), fill lpis acreages
                    addedAcreagesForParcel.forEach( addedAcreage => {

                        let existingAcreageOfSameKultura = acreages.find(acreage => acreage.parcelId === addedAcreage.parcelId
                                && acreage.lpisKultura.id === addedAcreage.lpisKultura.id)

                        if (!existingAcreageOfSameKultura) {
                            //if not fieldblock of context subject then we need not to remove kultura
                            //but set to 0 to allow user change
                            if (contextSubjectLpisId !== addedAcreage.lpisUserId
                                //but only if exists also another fieldblock part used by context subject
                                && addedAcreagesForParcel.find(
                                    acreageFP => contextSubjectLpisId && contextSubjectLpisId === acreageFP.lpisUserId)) {
                                addedAcreage.acreage = 0
                            }
                            //add whole acreage
                            acreages.push(addedAcreage)
                        } else {
                            //update existing acreage but only if acreage of same user does not exist
                            //to prevent duplicity because it was already added in
                            //addParcelAcreageToBarterParcelAcreages
                            if (contextSubjectLpisId !== addedAcreage.lpisUserId
                                && !addedAcreagesForParcel.find(acreageFP => contextSubjectLpisId && contextSubjectLpisId === acreageFP.lpisUserId)) {
                                let existingAcreageOfSameKulturaCopy = cloneDeep(existingAcreageOfSameKultura)
                                existingAcreageOfSameKulturaCopy.acreage += addedAcreage.acreage
                                let existingAcreageOfSameKulturaIndex = acreages.findIndex(acreage => acreage.parcelId === addedAcreage.parcelId
                                    && acreage.lpisKultura.id === addedAcreage.lpisKultura.id)
                                acreages[existingAcreageOfSameKulturaIndex] = existingAcreageOfSameKulturaCopy
                            }
                        }
                    })
                } else if (acreagesForParcel?.length === 0) {
                    //otherwise use one cadastral acreage
                    acreages.push({
                        lpisKultura: {id: 5, name: 'neurčená kultura'},
                        lpisKulturaId: 5,
                        barterId: barterParcel.barterId,
                        parcel: barterParcel.parcel,
                        parId: barterParcel.parcel.parId,
                        parcelId: barterParcel.parcel.id,
                        acreage: barterParcel.parcel.vymera
                    })
                }
            })
        })

        //ceil acreage if acreage is higher than parcel cadastral acreage
        acreages.forEach((acreage) => {
            if (acreage.parcel && acreage.acreage > acreage.parcel.vymera) {
                acreage.acreage = acreage.parcel.vymera
            }
        })
    }

    /**
     * Check if any LPIS user id from array is contained in list of concerned subjects lpisUserIds
     * @param lpisUserIds
     * @param concernedSubjects
     * @return boolean
     */
    isAnyConcernedLpisUserIdIncluded(lpisUserIds, concernedSubjects): boolean {
        return concernedSubjects
            .filter(concernedSubject => concernedSubject.lpisUserId)
            .map(concernedSubject => concernedSubject.lpisUserId)
            .some(lpisUserId => lpisUserIds.includes(lpisUserId))
    }

    /**
     * Check if subject id is contained in list of concerned users
     * @param concernedUsers
     * @param targetId
     * @returns {*}
     */
    isConcernedUser(concernedUsers, targetId): any {
        return concernedUsers.find((subject) => subject.id === targetId)
    }

    /**
     * Check if any subject from array is contained in list of subjects
     * @param concernedUsers
     * @param concernedSubjects
     */
    isAnyConcernedUser(concernedUsers, concernedSubjects): any {
        return concernedUsers.find(subject =>
            concernedSubjects.find(concerned => subject.id === concerned.subject.id)
        )
    }

    calculateBarterAcreagesSum(barterAcreages): number {
        return barterAcreages.reduce((sum, ba) => sum + this.toAcreageNumber(ba.acreage), 0)
    }

    initNewBarterVersion(barter, prevId, appendixNumber?): void {
        barter.appendixNumber = appendixNumber
        barter.barterYear = null
        barter.efficiencyFromDate = null
        barter.expirationDate = null
        barter.reusedPreviousExpiration = true
        barter.id = null
        barter.previousVersionId = prevId
        barter.state = BARTER_STATE.proposal
        barter.stateName = BARTER_STATE_LABEL.proposal
        barter.versionType = VERSION_TYPE.appendix
    }

    /**
     * Rebuild received array of parcelUsedAcreage entities to use it
     * in update parcel used acreage dialog
     *
     * @param parId
     * @param parcelUsedAcreages
     * @param barter
     * @param type
     * @param relatedParcelAcreages
     * @param newParcel
     * @returns {{parcelId: null, parId: null, items: {}}}
     */
    buildParcelUsedAcreagesForUpdate(parId: number, parcelUsedAcreages, barter, type, relatedParcelAcreages, newParcel): any {
        //filter out parcelUsedAcreages not related to lpis user of relevant barter side
        let contextLpisUser = this.getContextLpisUser(type, barter.concernedSubject, barter.counterparty)
        parcelUsedAcreages = parcelUsedAcreages.filter(acreage => acreage.lpisUserId === contextLpisUser)

        const result = {
            parcelId: newParcel.id,
            parId: newParcel.parId,
            items: []
        }

        // adding items from ParcelSubjectKulturaUsedAcreage
        // this part handles cases inside lpis block
        parcelUsedAcreages.forEach((item) => {
            if (!result.items[item.lpisKulturaId]) {
                // create new item from new parcel acreage
                result.items[item.lpisKulturaId] = item

                // find existing parcel acreage (from old parcel)
                const correspondingParcelAcreage = relatedParcelAcreages
                    .filter(rpa => rpa.parId === parId)
                    .find(rpa => item.lpisKulturaId === rpa.lpisKultura.id)
                // add old parcel acreage if there is existing parcel acreage
                result.items[item.lpisKulturaId].currentUsedAcreage
                    = (correspondingParcelAcreage) ? correspondingParcelAcreage.acreage : 0
            } else {
                // if kultura item already exists add only used acreage
                result.items[item.lpisKulturaId].acreage += item.acreage
            }
        })

        // this part handles cases without ParcelSubjectKulturaUsedAcreage - outside lpis block
        // run only if there is no ParcelSubjectKulturaUsedAcreage
        if (!result?.items?.length) {
            relatedParcelAcreages.filter(rpa => rpa.parId === parId).forEach(item => {
                    if (!result.items[item.lpisKulturaId]) {
                        // create new item from new parcel acreage
                        result.items[item.lpisKulturaId] = {
                            lpisKultura: item.lpisKultura,
                            lpisKulturaId: item.lpisKulturaId,
                            currentUsedAcreage: item.acreage,
                            acreage: 0,
                            // parcel and new acreage are data from new parcel
                            parcel: newParcel,
                            parId: newParcel.parId,
                            parcelId: newParcel.id
                        }
                        // lpisKulturaId with id 5 is 'neurčená kultura' - manually set kultura for part of parcel outside the LPIS
                        // see BarterService.normalizeParcelAcreages
                        // and if item.lpisKulturaId is not 5, than it is ParcelSubjectKulturaUsedAcreage from the old parcel
                        // but the new parcel does not have ParcelSubjectKulturaUsedAcreage (parcelUsedAcreages was empty)
                        // so we dont want to set used acreage of new part of parcel with lpisKulturaId <> 5
                        if (item.lpisKulturaId === 5) {
                            result.items[item.lpisKulturaId].acreage = newParcel.vymera
                        }
                    } else {
                        // if kultura item already exists add old acreage
                        result.items[item.lpisKulturaId].currentUsedAcreage += item.acreage
                    }
                })

            // this is special condition used when old parcel was in the LPIS but the new parcel is outside the LPIS
            // in this case, there will be items in result.items with kulturas from old parcel (but with null used acreage)
            // and this condition adds to result.items 'neurčená kultura' item for outside the LPIS part of new parcel
            if (result.items?.some(key => key !== '5') && !result.items?.[5]) {
                result.items[5] = {
                    lpisKultura: {id: 5, name: 'neurčená kultura'},
                    lpisKulturaId: 5,
                    currentUsedAcreage: 0,
                    parcel: newParcel,
                    parcelId: newParcel.id,
                    acreage: newParcel.vymera,
                    parId: newParcel.parId
                }
            }
        }

        return result
    }

    /**
     * Convert list where barter parcel contains acreages to list of just acreages
     * @param barterParcelWithAcreages
     */
    convertBarterParcelWithAcreagesToAcreages(barterParcelWithAcreages, contextLpisUserId: number): any[] {
        return barterParcelWithAcreages.flatMap(barterParcelGroup =>
            barterParcelGroup.parcels.flatMap(barterParcel =>
                barterParcel.stgImportGeomParcelAcreages.map(barterParcelAcreage => {
                    barterParcelAcreage.parcelId = barterParcel.parcel.id
                    barterParcelAcreage.parId = barterParcel.parcel.parId
                    barterParcelAcreage.parcel = barterParcel.parcel
                    barterParcelAcreage.barterId = barterParcel.barterId
                    barterParcelAcreage.lpisUserId = contextLpisUserId
                    return barterParcelAcreage
                })
            )
        )
    }

    /**
     * Return true if value.lpisUserId is the same as lpisUser of corresponding barter side (if exists)
     *
     * @param type
     * @param concernedSubject
     * @param counterparty
     * @param parcelUsedAcreage
     * @returns {*|boolean}
     * @private
     */
    private isContextLpisUser(type, concernedSubject, counterparty, parcelUsedAcreage): boolean {
        if (type === BARTER_PARCEL_TYPE.passed || type === BARTER_PARCEL_TYPE.received) {
            return true
        }
        const lpisUserId = this.getContextLpisUser(type, concernedSubject, counterparty)
        return lpisUserId && lpisUserId === parcelUsedAcreage?.lpisUserId
    }

    /**
     * Výměra za parcelu je v datovém modelu celé číslo (`int64`). Nečitelnou hodnotu bere jako
     * nulu, aby jedna rozbitá parcela neotrávila celý součet — ale zapíše to do konzole, ať to
     * není tichá chyba.
     *
     * Dřív to bylo napsané dvakrát a lišilo se to v jednom znaku: tady bylo `parseInt(...) || 0`,
     * v `calculateBarterAcreagesSum` jen `parseInt(...)`. Jedna nečitelná výměra proto ukázala za
     * parcelu 0 a v celkovém součtu `NaN` — dvě různá špatná čísla na jedné obrazovce (DEV-2246).
     */
    private toAcreageNumber(value: unknown): number {
        const parsed = parseInt(value as string, 10)

        if (isNaN(parsed)) {
            console.warn('Nečitelná výměra ve směně, do součtu jde jako nula:', value)

            return 0
        }

        return parsed
    }
}
